import React, { useEffect, useState, useRef } from 'react';
import { Button, Form, Message, Table } from 'semantic-ui-react';
import { request, units, yuan, time } from './helpers';

export default function PricingOperations({ prices, onRefresh }) {
  const [selected, setSelected] = useState([]);
  const [filter, setFilter] = useState('');
  const [strategy, setStrategy] = useState('margin');
  const [factor, setFactor] = useState('30');
  const [fixed, setFixed] = useState({
    input: '1',
    cacheWrite: '1.25',
    cacheRead: '0.1',
    output: '2',
  });
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [costs, setCosts] = useState([]);
  const [cost, setCost] = useState({
    channel_id: '',
    model: '',
    input: '',
    cacheWrite: '',
    cacheRead: '',
    output: '',
    basis: 'usage',
    reason: '',
  });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [rollback, setRollback] = useState('');
  useEffect(() => {
    let active = true;
    request('get', '/api/platform/admin/costs')
      .then((v) => {
        if (active) setCosts(v);
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, []);
  const generation = useRef(0);
  const change = (work) => {
    generation.current++;
    work();
    setPreview(null);
  };
  const run = async (work) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await work();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const active = prices.filter(
    (p) =>
      p.enabled &&
      `${p.model} ${p.provider}`.toLowerCase().includes(filter.toLowerCase())
  );
  const payload = () => {
    const value = Number(factor);
    if (!Number.isFinite(value)) throw new Error('请输入有效策略参数');
    return {
      ids: selected,
      strategy,
      basis_points: Math.round(value * (strategy === 'margin' ? 100 : 10000)),
      input_rate: units(fixed.input),
      cache_write_rate: units(fixed.cacheWrite),
      cache_read_rate: units(fixed.cacheRead),
      output_rate: units(fixed.output),
      reason,
    };
  };

  return (
    <section className='pricing-operations'>
      <h2>价格运维</h2>
      <p>
        金额单位：元 / 百万
        Token。策略以模型基准成本计算建议售价；实际调用成本按渠道成本版本核算。缺少渠道成本时沿用模型成本。
      </p>
      {error && <Message negative content={error} />}
      {notice && <Message positive content={notice} />}
      <h3>渠道采购成本</h3>
      <Form
        onSubmit={() =>
          run(async () => {
            const channel = Number(cost.channel_id);
            if (!Number.isInteger(channel) || channel <= 0)
              throw new Error('渠道 ID 必须是正整数');
            await request('post', '/api/platform/admin/costs', {
              channel_id: channel,
              model: cost.model,
              input_rate: units(cost.input),
              cache_write_rate: units(cost.cacheWrite),
              cache_read_rate: units(cost.cacheRead),
              output_rate: units(cost.output),
              basis: cost.basis,
              reason: cost.reason,
            });
            setCosts(await request('get', '/api/platform/admin/costs'));
            setNotice('渠道成本新版本已保存，仅影响后续调用');
          })
        }
      >
        <Form.Group widths='equal'>
          {[
            ['channel_id', '渠道 ID'],
            ['model', '对外模型名'],
            ['input', '输入成本'],
            ['cacheWrite', 'Cache Write 成本'],
            ['cacheRead', 'Cache Read 成本'],
            ['output', '输出成本'],
          ].map(([key, label]) => (
            <Form.Input
              key={key}
              required
              label={label}
              value={cost[key]}
              onChange={(_, v) => setCost({ ...cost, [key]: v.value })}
            />
          ))}
        </Form.Group>
        <Form.Group widths='equal'>
          <Form.Select
            label='成本口径'
            value={cost.basis}
            options={[
              { key: 'usage', value: 'usage', text: '按量采购成本' },
              {
                key: 'allocated',
                value: 'allocated',
                text: '套餐分摊估算成本',
              },
            ]}
            onChange={(_, v) => setCost({ ...cost, basis: v.value })}
          />
          <Form.Input
            required
            label='调整原因'
            maxLength={512}
            value={cost.reason}
            onChange={(_, v) => setCost({ ...cost, reason: v.value })}
          />
        </Form.Group>
        <Button primary disabled={busy}>
          保存成本版本
        </Button>
      </Form>
      <div className='platform-scroll'>
        <Table unstackable compact>
          <Table.Header>
            <Table.Row>
              {[
                '成本版本',
                '渠道 / 模型',
                '输入 / Cache Write / Cache Read / 输出成本',
                '口径',
                '原因 / 时间',
              ].map((s) => (
                <Table.HeaderCell key={s}>{s}</Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {costs.map((c) => (
              <Table.Row key={c.id}>
                <Table.Cell>#{c.id}</Table.Cell>
                <Table.Cell>
                  {c.channel_id} / {c.model}
                </Table.Cell>
                <Table.Cell>
                  {yuan(c.input_rate)} / {yuan(c.cache_write_rate)} /{' '}
                  {yuan(c.cache_read_rate)} / {yuan(c.output_rate)}
                </Table.Cell>
                <Table.Cell>
                  {c.basis === 'allocated' ? '分摊估算' : '按量'}
                </Table.Cell>
                <Table.Cell>
                  {c.reason}
                  <br />
                  {time(c.created_at)}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
      <h3>批量调价</h3>
      <Form
        onSubmit={() =>
          run(async () => {
            const snapshot = payload();
            const version = generation.current;
            const rows = await request(
              'post',
              '/api/platform/admin/prices/preview',
              snapshot
            );
            if (version === generation.current)
              setPreview({ request: snapshot, rows });
          })
        }
      >
        <Form.Input
          label='按供应商或模型筛选'
          value={filter}
          onChange={(_, v) => setFilter(v.value)}
        />
        <Button
          type='button'
          onClick={() => change(() => setSelected(active.map((p) => p.id)))}
        >
          选择筛选结果
        </Button>
        <Button type='button' onClick={() => change(() => setSelected([]))}>
          清空选择
        </Button>
        <div className='pricing-selection'>
          {active.map((p) => (
            <Form.Checkbox
              key={p.id}
              label={`${p.model} · #${p.id} · ${yuan(p.input_rate)} / ${yuan(
                p.cache_write_rate
              )} / ${yuan(p.cache_read_rate)} / ${yuan(p.output_rate)}`}
              checked={selected.includes(p.id)}
              onChange={() =>
                change(() =>
                  setSelected(
                    selected.includes(p.id)
                      ? selected.filter((id) => id !== p.id)
                      : [...selected, p.id]
                  )
                )
              }
            />
          ))}
        </div>
        <Form.Group widths='equal'>
          <Form.Select
            label='售价策略'
            value={strategy}
            options={[
              { key: 'margin', value: 'margin', text: '目标毛利率' },
              { key: 'multiplier', value: 'multiplier', text: '成本倍率' },
              { key: 'fixed', value: 'fixed', text: '固定售价' },
            ]}
            onChange={(_, v) =>
              change(() => {
                setStrategy(v.value);
                setFactor(v.value === 'margin' ? '30' : '1.5');
              })
            }
          />
          {strategy !== 'fixed' ? (
            <Form.Input
              label={
                strategy === 'margin'
                  ? '毛利率（%，0–99.99）'
                  : '倍率（例如 1.5）'
              }
              value={factor}
              onChange={(_, v) => change(() => setFactor(v.value))}
            />
          ) : (
            <>
              <Form.Input
                label='输入售价'
                value={fixed.input}
                onChange={(_, v) =>
                  change(() => setFixed({ ...fixed, input: v.value }))
                }
              />
              <Form.Input
                label='输出售价'
                value={fixed.output}
                onChange={(_, v) =>
                  change(() => setFixed({ ...fixed, output: v.value }))
                }
              />
              <Form.Input
                label='Cache Write 售价'
                value={fixed.cacheWrite}
                onChange={(_, v) =>
                  change(() => setFixed({ ...fixed, cacheWrite: v.value }))
                }
              />
              <Form.Input
                label='Cache Read 售价'
                value={fixed.cacheRead}
                onChange={(_, v) =>
                  change(() => setFixed({ ...fixed, cacheRead: v.value }))
                }
              />
            </>
          )}
        </Form.Group>
        <Form.Input
          required
          label='调整原因'
          maxLength={512}
          value={reason}
          onChange={(_, v) => change(() => setReason(v.value))}
        />
        <Button primary disabled={busy || !selected.length}>
          预览 {selected.length} 个模型
        </Button>
      </Form>
      {preview && (
        <>
          <div className='platform-scroll'>
            <Table unstackable>
              <Table.Header>
                <Table.Row>
                  {['模型 / 旧版本', '旧售价 → 新售价', '基准成本', '提醒'].map(
                    (s) => (
                      <Table.HeaderCell key={s}>{s}</Table.HeaderCell>
                    )
                  )}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {preview.rows.map((r) => (
                  <Table.Row key={r.previous.id}>
                    <Table.Cell>
                      {r.previous.model} / #{r.previous.id}
                    </Table.Cell>
                    <Table.Cell>
                      {yuan(r.previous.input_rate)} /{' '}
                      {yuan(r.previous.cache_write_rate)} /{' '}
                      {yuan(r.previous.cache_read_rate)} /{' '}
                      {yuan(r.previous.output_rate)} → {yuan(r.next.input_rate)}{' '}
                      / {yuan(r.next.cache_write_rate)} /{' '}
                      {yuan(r.next.cache_read_rate)} /{' '}
                      {yuan(r.next.output_rate)}
                    </Table.Cell>
                    <Table.Cell>
                      {yuan(r.next.cost_input_rate)} /{' '}
                      {yuan(r.next.cost_cache_write_rate)} /{' '}
                      {yuan(r.next.cost_cache_read_rate)} /{' '}
                      {yuan(r.next.cost_output_rate)}
                    </Table.Cell>
                    <Table.Cell>{r.warnings.join('；') || '—'}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
          <Button
            primary
            disabled={busy}
            onClick={() =>
              run(async () => {
                await request(
                  'post',
                  '/api/platform/admin/prices/publish',
                  preview.request
                );
                setPreview(null);
                setSelected([]);
                await onRefresh();
                setNotice('价格已原子发布，历史账单保持原价格');
              })
            }
          >
            确认发布以上价格
          </Button>
        </>
      )}
      <h3>价格历史与回滚</h3>
      <Form.Input
        label='回滚原因（回滚会发布新版本）'
        value={rollback}
        maxLength={512}
        onChange={(_, v) => setRollback(v.value)}
      />
      <div className='platform-scroll'>
        <Table unstackable compact>
          <Table.Header>
            <Table.Row>
              {[
                '版本 / 模型',
                '售价',
                '基准成本',
                '策略 / 参数',
                '时间 / 操作人 / 原因',
                '操作',
              ].map((s) => (
                <Table.HeaderCell key={s}>{s}</Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {prices.map((p) => (
              <Table.Row key={p.id}>
                <Table.Cell>
                  #{p.id} {p.model}
                  <br />
                  {p.enabled ? '当前' : '历史'}
                </Table.Cell>
                <Table.Cell>
                  {yuan(p.input_rate)} / {yuan(p.cache_write_rate)} /{' '}
                  {yuan(p.cache_read_rate)} / {yuan(p.output_rate)}
                </Table.Cell>
                <Table.Cell>
                  {yuan(p.cost_input_rate)} / {yuan(p.cost_cache_write_rate)} /{' '}
                  {yuan(p.cost_cache_read_rate)} / {yuan(p.cost_output_rate)}
                </Table.Cell>
                <Table.Cell>
                  {{
                    margin: '毛利率',
                    multiplier: '成本倍率',
                    fixed: '固定售价',
                    rollback: '回滚',
                  }[p.strategy] || '手动'}{' '}
                  {p.strategy === 'margin'
                    ? `${p.basis_points / 100}%`
                    : p.strategy === 'multiplier'
                    ? `${p.basis_points / 10000}倍`
                    : ''}
                </Table.Cell>
                <Table.Cell>
                  {time(p.created_at)} / {p.created_by}
                  <br />
                  {p.reason || '旧版本未记录原因'}
                </Table.Cell>
                <Table.Cell>
                  <Button
                    type='button'
                    size='mini'
                    disabled={
                      busy ||
                      p.enabled ||
                      !rollback.trim() ||
                      !prices.some((v) => v.model === p.model && v.enabled)
                    }
                    onClick={() =>
                      run(async () => {
                        const current = prices.find(
                          (v) => v.model === p.model && v.enabled
                        );
                        await request(
                          'post',
                          `/api/platform/admin/prices/${p.id}/rollback`,
                          { current_id: current.id, reason: rollback }
                        );
                        await onRefresh();
                        setPreview(null);
                        setNotice('已发布回滚版本');
                      })
                    }
                  >
                    回滚到此价格
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    </section>
  );
}
